import { MAPS_BROWSER_KEY } from './env';

/** One autocomplete prediction, reduced to the two fields step 1 uses. */
export type Suggestion = {
  /** Opaque Google id. The ONLY thing sent to `resolveAddress`; see ADR-0004. */
  placeId: string;
  /** What to show in the dropdown. Display only, never sent anywhere. */
  text: string;
};

/**
 * Places Autocomplete, through the Maps JavaScript API.
 *
 * WHY NOT A PLAIN `fetch` TO THE PLACES REST API, which would be far simpler and
 * would need no script tag at all: that endpoint sends no
 * `Access-Control-Allow-Origin`, so the browser blocks it. The Maps JS
 * "Place Autocomplete Data" API exists precisely because of that, and is the
 * only supported browser path.
 *
 * Neither reference repo does this. `dac-qualifier` has a plain text input and
 * geocodes server-side against the Census; `generator-sales-tool` has plain
 * address fields. The loader below is adapted from the latter's
 * `web/src/lib/maps/mapsLoader.ts`; everything under it is new.
 */

let loadPromise: Promise<void> | null = null;

const CALLBACK = '__ksStudioMapsReady__';

/**
 * Idempotent loader for the Maps JavaScript API.
 *
 * RESOLUTION IS GATED ON GOOGLE'S `callback`, NOT ON THE SCRIPT'S `onload`, and
 * the difference is a real race rather than a style preference: with
 * `loading=async` the base script attaches `google.maps.importLibrary` AFTER
 * onload fires, so resolving on onload hands the caller a `google.maps` that
 * cannot import anything yet. Carried over from generator-sales-tool, where the
 * same comment sits on the same line.
 *
 * Rejects cleanly when no key is set, so a caller can fall back to a plain input
 * rather than crashing the widget.
 */
export function loadMaps(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_BROWSER_KEY) {
      reject(new Error('VITE_MAPS_BROWSER_KEY is not set'));
      return;
    }
    if (
      typeof google !== 'undefined' &&
      typeof google.maps?.importLibrary === 'function'
    ) {
      resolve();
      return;
    }
    (window as unknown as Record<string, () => void>)[CALLBACK] = () => resolve();
    const params = new URLSearchParams({
      key: MAPS_BROWSER_KEY,
      v: 'weekly',
      loading: 'async',
      callback: CALLBACK,
    });
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      // Allow a retry after a transient network failure. Without this the
      // widget is permanently addressless for the rest of the page's life.
      loadPromise = null;
      reject(new Error('Failed to load the Google Maps JavaScript API'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * Fetch predictions for what the customer has typed.
 *
 * NO SESSION TOKEN IS PASSED, and that is deliberate rather than an oversight.
 *
 * Google bills autocomplete two ways. When a session token ties the typing to a
 * closing Place Details call, the typing is free and only the details call is
 * charged. When it does not, the typing is charged per request. Pairing is
 * impossible here: `AutocompleteRequest.sessionToken` takes an opaque
 * `AutocompleteSessionToken` instance with no string accessor, and the only
 * documented way to close its session is calling `fetchFields()` IN THE BROWSER.
 * Doing that would mean the client supplying the address the service-area gate
 * runs on, which is exactly what ADR-0004 exists to prevent.
 *
 * So `resolveAddress` keeps doing Place Details server-side, and the
 * `sessionToken` on the wire is a UUID this app mints that Maps JS never sees.
 * Passing a token here that can never be closed would bill identically (Google
 * charges an abandoned session as if no token were given) while implying a
 * pairing that does not exist.
 *
 * @param input - the raw text typed so far. Callers debounce.
 * @returns predictions, or an empty array for a blank input.
 */
export async function fetchSuggestions(input: string): Promise<Suggestion[]> {
  if (!input.trim()) return [];
  await loadMaps();
  const { AutocompleteSuggestion } = (await google.maps.importLibrary(
    'places',
  )) as google.maps.PlacesLibrary;

  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input,
    // Kasselman installs in New York only, so anything outside the US is noise
    // the customer has to scroll past and a request we pay for. A tighter bias
    // to New York is possible and deliberately not done yet: the gate is the
    // authority on service area (ADR-0010), and narrowing the picker to match it
    // would put the same rule in two places.
    includedRegionCodes: ['us'],
  });

  return suggestions
    .map((s) => s.placePrediction)
    .filter((p): p is google.maps.places.PlacePrediction => p !== null)
    .map((p) => ({ placeId: p.placeId, text: p.text.toString() }));
}

/** Test seam. Clears the module-level cache so a test can re-drive the loader. */
export function resetMapsLoader(): void {
  loadPromise = null;
}
